import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { WebsiteAuditService } from '../website-audit.service';
import {
  AgentKey,
  AgentOption,
  ExperimentPage,
  ExperimentResponse,
  VisibilityFilter,
} from './agentic-ecommerce.types';
import {
  AGENTS,
  calculateVisibilityScore,
  getAgentMetadata,
  getProducts,
  getScoreColor,
  isProductMissingForPage,
} from './agentic-ecommerce.utils';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-agentic-ecommerce',
  templateUrl: './agentic-ecommerce.component.html',
  styleUrls: ['./agentic-ecommerce.component.css'],
})
export class AgenticEcommerceComponent implements OnInit, OnDestroy {
  constructor(
    private websiteAuditService: WebsiteAuditService,
    private targetService: CoreTargetService,
    private route: ActivatedRoute,
  ) {}

  private subs: Subscription = new Subscription();
  private lastAuditValue: string = '';
  private selectedTask: any = null;
  private target: string = '';
  private lastTarget: string = '';

  public experiment: ExperimentResponse | null = null;
  public isLoading: boolean = false;
  public errorMessage: string = '';
  public selectedAgentKey: AgentKey = '';
  public productFilter: VisibilityFilter = 'all';

  public skeletonAgentCards: number[] = [1, 2, 3, 4];
  public skeletonPageCards: number[] = [1, 2];
  public skeletonProducts: number[] = [1, 2, 3, 4, 5, 6];

  private token: string = '';

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') as string;
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        const audit = this.getAuditValue(task);

        this.target =
          this.targetService.getSelectedTarget('target') ||
          sessionStorage.getItem('target') ||
          '';

        if (!audit) {
          this.resetExperimentState();
          return;
        }

        if (
          audit === this.lastAuditValue &&
          this.experiment &&
          this.target === this.lastTarget
        ) {
          return;
        }

        this.lastTarget = this.target;
        this.lastAuditValue = audit;
        this.loadExperiment(audit);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public selectAgent(agentKey: AgentKey): void {
    const agent = this.agentOptions.find((option) => option.key === agentKey);

    if (!agent || agent.disabled) {
      return;
    }

    this.selectedAgentKey = agentKey;
  }

  public setProductFilter(filter: VisibilityFilter): void {
    this.productFilter = filter;
  }

  public retry(): void {
    const audit = this.getAuditValue(this.selectedTask);

    if (!audit) {
      this.errorMessage = 'No audit is selected yet.';
      return;
    }

    this.loadExperiment(audit);
  }

  public get agentOptions(): AgentOption[] {
    const modelExtractions = this.modelExtractions;
    const knownAgentKeys = Object.keys(AGENTS);
    const extraAgentKeys = Object.keys(modelExtractions).filter(
      (agentKey) => !knownAgentKeys.includes(agentKey),
    );
    const agentKeys = [...knownAgentKeys, ...extraAgentKeys];

    return agentKeys.map((agentKey) => {
      const metadata = getAgentMetadata(agentKey);
      const disabled = !modelExtractions[agentKey];
      const totalMatchedProducts = Number(
        modelExtractions[agentKey]?.totalMatchedProducts || 0,
      );
      const score = calculateVisibilityScore(
        totalMatchedProducts,
        this.totalProducts,
      );

      return {
        ...metadata,
        key: agentKey,
        score,
        scoreColor: disabled ? '#6f6a65' : getScoreColor(score),
        totalMatchedProducts,
        totalProducts: this.totalProducts,
        disabled,
      };
    });
  }

  public get selectedAgentLabel(): string {
    if (!this.selectedAgentKey) {
      return 'the selected agent';
    }

    return getAgentMetadata(this.selectedAgentKey).label;
  }

  public get pages(): ExperimentPage[] {
    return this.experiment?.htmlExtraction?.pages || [];
  }

  public get visiblePages(): ExperimentPage[] {
    if (!this.selectedAgentKey) {
      return [];
    }

    return this.pages.filter((page) => {
      const products = getProducts(page);

      if (!products.length) {
        return false;
      }

      if (this.productFilter === 'missing') {
        return products.some((product) =>
          isProductMissingForPage(
            product,
            this.selectedAgentKey,
            page.pageType,
          ),
        );
      }

      return true;
    });
  }

  public get hasProducts(): boolean {
    return this.pages.some((page) => getProducts(page).length > 0);
  }

  public get hasAgents(): boolean {
    return this.agentOptions.some((agent) => !agent.disabled);
  }

  public get totalProducts(): number {
    const total = Number(this.experiment?.total || 0);
    return Number.isFinite(total) && total > 0 ? total : 0;
  }

  public get modelExtractions(): Record<
    AgentKey,
    { totalMatchedProducts?: number }
  > {
    return this.experiment?.modelExtractions || {};
  }

  public trackByAgent(index: number, agent: AgentOption): AgentKey {
    return agent.key || String(index);
  }

  public trackByPage(index: number, page: ExperimentPage): string {
    return page.pageUrl || page.pagePath || page.pageTitle || String(index);
  }

  private loadExperiment(audit: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    console.log(this.token);

    this.subs.add(
      this.websiteAuditService
        .getCrawlerExperiment(audit, this.token)
        .subscribe({
          next: (response: ExperimentResponse) => {
            this.experiment = response || {};
            this.isLoading = false;
            this.syncSelectedAgent();
          },
          error: (err: any) => {
            this.experiment = null;
            this.isLoading = false;
            this.errorMessage =
              err?.error?.message ||
              'We could not load the agentic ecommerce experiment.';
          },
        }),
    );
  }

  private syncSelectedAgent(): void {
    const agentKeys = this.agentOptions
      .filter((agent) => !agent.disabled)
      .map((agent) => agent.key);

    if (!agentKeys.length) {
      this.selectedAgentKey = '';
      return;
    }

    if (this.selectedAgentKey && agentKeys.includes(this.selectedAgentKey)) {
      return;
    }

    this.selectedAgentKey = agentKeys.includes('openai')
      ? 'openai'
      : agentKeys[0];
  }

  private resetExperimentState(): void {
    this.lastAuditValue = '';
    this.experiment = null;
    this.errorMessage = '';
    this.selectedAgentKey = '';
    this.productFilter = 'all';
    this.isLoading = false;
  }

  private getAuditValue(task: any): string {
    return String(task?.url || task?.taskId || '');
  }
}
